import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, PackagePlus, Pencil } from 'lucide-react'

import {
    PurchaseLotStatusBadge,
    purchaseLotInReasonLabel,
} from '@/components/purchase-lots/purchase-lot-badges'
import { PurchaseLotNoteActions } from '@/components/purchase-lots/purchase-lot-note-actions'
import { PurchaseLotNoteItemsTable } from '@/components/purchase-lots/purchase-lot-note-items-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import {
    useApprovePurchaseInNote,
    useCancelPurchaseInNote,
    usePurchaseInNote,
    useRejectPurchaseInNote,
} from '@/hooks/use-purchase-lot-query'
import { usePrintPurchaseLotLabel } from '@/hooks/use-lot-label-print'
import { formatDateTimeVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/purchase-lots/stock-in-notes/$id',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const { data, isLoading } = usePurchaseInNote(id)
    const note = data?.data
    const { user, hasPermission } = useAuth()
    const approve = useApprovePurchaseInNote()
    const reject = useRejectPurchaseInNote()
    const cancel = useCancelPurchaseInNote()
    const printLot = usePrintPurchaseLotLabel()

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = user?.id && note.created_by_id === user.id
    const canApprove = hasPermission?.('purchase_lots.approve_in') ?? true
    const canWrite = hasPermission?.('purchase_lots.write') ?? true

    return (
        <div className="space-y-4 p-4 md:p-6">
            <Button
                asChild
                variant="ghost"
                className="h-11 md:h-9 -ml-2 md:ml-0"
            >
                <Link to="/purchase-lots/stock-in-notes" search={{ page: 1 }}>
                    <ArrowLeft className="size-4 mr-1" />
                    {m.sl_lot_back()}
                </Link>
            </Button>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <PackagePlus className="size-5" />
                            <CardTitle className="font-mono text-base md:text-2xl">
                                {note.code}
                            </CardTitle>
                            <PurchaseLotStatusBadge status={note.status} />
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {note.status === 'pending' &&
                                !!isCreator &&
                                canWrite &&
                                !note.stocktake_session_id && (
                                    <Button
                                        asChild
                                        variant="outline"
                                        className="h-11 md:h-9"
                                    >
                                        <Link
                                            to="/purchase-lots/stock-in-notes/$id/edit"
                                            params={{ id }}
                                        >
                                            <Pencil className="size-4 mr-1" />
                                            {m.pl_pnk_edit_btn()}
                                        </Link>
                                    </Button>
                                )}
                            <PurchaseLotNoteActions
                                status={note.status}
                                canApprove={canApprove}
                                canCancel={!!isCreator && canWrite}
                                approvePending={approve.isPending}
                                rejectPending={reject.isPending}
                                cancelPending={cancel.isPending}
                                approveConfirm={m.pl_pnk_confirm_approve()}
                                cancelConfirm={m.pl_pnk_confirm_cancel()}
                                rejectTitle={m.pl_pnk_reject_title()}
                                onApprove={() => approve.mutateAsync(id)}
                                onReject={(reason) =>
                                    reject.mutateAsync({ id, reason })
                                }
                                onCancel={() => cancel.mutateAsync(id)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-3 grid-cols-2">
                    <div className="col-span-2">
                        <Field
                            label={m.sl_detail_label_reason()}
                            value={purchaseLotInReasonLabel(note.reason)}
                        />
                    </div>
                    <Field
                        label={m.sl_detail_label_created_at()}
                        value={formatDateTimeVN(note.created_at)}
                    />
                    <Field
                        label={m.sl_detail_label_created_by()}
                        value={note.created_by_name ?? '—'}
                    />
                    {note.approved_at && (
                        <>
                            <Field
                                label={m.sl_detail_label_approved_at()}
                                value={formatDateTimeVN(note.approved_at)}
                            />
                            <Field
                                label={m.sl_detail_label_approver()}
                                value={note.approved_by_name ?? '—'}
                            />
                        </>
                    )}
                    {note.cancelled_at && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_cancelled_at()}
                                value={formatDateTimeVN(note.cancelled_at)}
                            />
                        </div>
                    )}
                    {note.notes && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_notes()}
                                value={note.notes}
                            />
                        </div>
                    )}
                    {note.rejected_reason && (
                        <div className="col-span-2">
                            <Field
                                label={m.sl_detail_label_rejected_reason()}
                                value={note.rejected_reason}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            <PurchaseLotNoteItemsTable
                items={note.items ?? []}
                onPrintLot={(id) => printLot.mutate(id)}
                printing={printLot.isPending}
                canPrint={note.status === 'approved'}
            />
        </div>
    )
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-base font-medium md:text-sm">{value}</p>
        </div>
    )
}
